import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_ITEMS } from '../../utils/constants';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Menu, X } from 'lucide-react';

const Navigation: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const { scrollPosition } = useScrollAnimation();
    const isScrolled = scrollPosition.y > 50;

    useEffect(() => {
        const handleScroll = () => {
            const sections = NAV_ITEMS.map(item => item.href.slice(1));
            const currentSection = sections.find(section => {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    return rect.top <= 100 && rect.bottom >= 100;
                }
                return false;
            });
            if (currentSection) {
                setActiveSection(currentSection);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavClick = (href: string) => {
        setIsOpen(false);
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled
                    ? 'glass backdrop-blur-md bg-white/80 shadow-lg'
                    : 'bg-transparent'
            }`}
        >
            <div className="container mx-auto px-4 md:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <motion.a
                        href="#home"
                        onClick={(e) => {
                            e.preventDefault();
                            handleNavClick('#home');
                        }}
                        className="relative z-10"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <img
                            src="/images/logos/桜ヶ丘電信.svg"
                            alt="桜ヶ丘電信"
                            className="h-10 w-auto"
                        />
                    </motion.a>

                    {/* Desktop Navigation */}
                    <div className="hidden md:block">
                        <div className="flex items-center space-x-1">
                            {NAV_ITEMS.map((item) => {
                                const isActive = activeSection === item.href.slice(1);

                                return (
                                    <motion.a
                                        key={item.label}
                                        href={item.href}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleNavClick(item.href);
                                        }}
                                        className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                                            isActive
                                                ? 'text-sky-600'
                                                : 'text-slate-700 hover:text-sky-600'
                                        }`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {item.label}
                                        {isActive && (
                                            <motion.div
                                                layoutId="activeNav"
                                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-400 to-sky-600"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.3 }}
                                            />
                                        )}
                                    </motion.a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <motion.button
                        onClick={() => setIsOpen(!isOpen)}
                        className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        whileTap={{ scale: 0.95 }}
                    >
                        <AnimatePresence mode="wait">
                            {isOpen ? (
                                <motion.div
                                    key="close"
                                    initial={{ rotate: -90 }}
                                    animate={{ rotate: 0 }}
                                    exit={{ rotate: 90 }}
                                >
                                    <X size={24} className="text-slate-700" />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="menu"
                                    initial={{ rotate: 90 }}
                                    animate={{ rotate: 0 }}
                                    exit={{ rotate: -90 }}
                                >
                                    <Menu size={24} className="text-slate-700" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.button>
                </div>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="md:hidden overflow-hidden"
                        >
                            <div className="py-4 space-y-2">
                                {NAV_ITEMS.map((item, index) => {
                                    const isActive = activeSection === item.href.slice(1);

                                    return (
                                        <motion.a
                                            key={item.label}
                                            href={item.href}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleNavClick(item.href);
                                            }}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                                isActive
                                                    ? 'bg-sky-50 text-sky-600'
                                                    : 'text-slate-700 hover:bg-slate-50 hover:text-sky-600'
                                            }`}
                                        >
                                            {item.label}
                                        </motion.a>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Progress Bar */}
            <motion.div
                className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-sky-400 to-purple-400"
                style={{ width: `${scrollPosition.y > 0 ? Math.min((scrollPosition.y / (document.body.scrollHeight - window.innerHeight)) * 100, 100) : 0}%` }}
                transition={{ duration: 0.1 }}
            />
        </nav>
    );
};

export default Navigation;